import React, { ReactElement, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'

import {
  Alert,
  Avatar,
  Box,
  Button,
  capitalize,
  Card,
  Chip,
  Dialog,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  selectRecertificationCycles,
  selectLicenseRecertificationCyclesStatus,
  fetchLicenseRecertificationCycles,
  selectNREMTRecertificationCyclesStatus,
  selectCredentialRecertificationCyclesStatus,
  fetchNREMTRecertificationCycles,
  fetchCredentialRecertificationCycles,
  selectLicenseRecertificationCycles,
  selectNREMTRecertificationCycles,
  selectCredentialRecertificationCycles,
  selectRecertSearch,
  selectRecertTypeFilter,
  selectRecertStatusFilter,
  selectRecertSort,
  recertSearchChanged,
  recertTypeFilterChanged,
  recertStatusFilterChanged,
  recertSortChanged,
  fetchUnstartedComplianceRecertItems,
  selectUnstartedComplianceRecertItems,
  selectUnstartedComplianceRecertItemsStatus,
} from 'src/v3/profile/profile.slice'
import {
  selectUseRecertificationCycle,
  selectUseCompliance,
} from 'src/v3/settings/settings.slice'

import {
  CalendarIcon,
  ClockIcon,
  CloseIcon,
  CourseIcon,
  InfoIcon,
  LightbulbIcon,
  ManagerIcon,
  RecertificationIcon,
  SearchIcon,
  VerifiedIcon,
} from 'src/v3/core/icons'
import AddIcon from '@mui/icons-material/Add'
import AlarmIcon from '@mui/icons-material/Alarm'
import CategoryIcon from '@mui/icons-material/Category'
import ZoomInIcon from '@mui/icons-material/ZoomIn'

import {
  CREDENTIAL_TYPES,
  RecertCycleType,
  RecertificationCycle,
} from 'src/v3/core/types'
import axios, { pluckData } from 'src/utilities/http-client'
import { useNotifications } from 'src/v3/hooks/use-notifications'
import { useAutoFetch } from 'src/v3/hooks/use-auto-fetch'
import BasePage from '../layout'
import { CreateCycleForm } from './components/cycle-form'
import { MultiCircularProgress } from './components/progress'

const createCredential = 'create'

export const licenseTypeDict: Record<string, string> = {
  paramedic: 'Paramedic',
  'registered nurse': 'Registered Nurse',
  emr: 'Emergency Medical Responder',
  cfr: 'Certified First Responder',
  emt: 'EMT',
  'emt-1': 'EMT-1',
  'emt-b': 'EMT-B',
  'emt-d': 'EMT-D',
  aemt: 'AEMT',
  'emt-2': 'EMT-2',
  'emt-cc': 'EMT-CC',
  'advanced care paramedic (canada only)':
    'Advanced Care Paramedic (Canada Only)',
  'primary care paramedic (canada only)':
    'Primary Care Paramedic (Canada Only)',
  physician: 'Physician',
  'physician assistant': 'Physician Assistant',
  other: 'Other',
}

export const getCycleStatus = (cycle: RecertificationCycle) =>
  dayjs(cycle.expirationDate).isBefore(dayjs())
    ? 'Archived'
    : cycle.complete
      ? 'Completed'
      : 'Active'

export default (): ReactElement => {
  let navigate = useNavigate()
  let dispatch = useAppDispatch()
  let { showError, NotificationComponent } = useNotifications()
  let useRecertificationCycle = useAppSelector(selectUseRecertificationCycle)
  let useCompliance = useAppSelector(selectUseCompliance)
  let unstartedComplianceRecertItems = useAppSelector(
    selectUnstartedComplianceRecertItems,
  )
  let unstartedComplianceRecertItemsStatus = useAppSelector(
    selectUnstartedComplianceRecertItemsStatus,
  )

  let cycles = useAppSelector(selectRecertificationCycles)
  let licenseCycles = useAppSelector(selectLicenseRecertificationCycles)
  let licenseCyclesStatus = useAppSelector(
    selectLicenseRecertificationCyclesStatus,
  )
  let credentialCycles = useAppSelector(selectCredentialRecertificationCycles)
  let credentialCyclesStatus = useAppSelector(
    selectCredentialRecertificationCyclesStatus,
  )
  let nremtCycles = useAppSelector(selectNREMTRecertificationCycles)
  let nremtCyclesStatus = useAppSelector(selectNREMTRecertificationCyclesStatus)

  let cycleFilter = useAppSelector(selectRecertSearch)
  let visibleType = useAppSelector(selectRecertTypeFilter)
  let statusFilter = useAppSelector(selectRecertStatusFilter)
  let cycleSort = useAppSelector(selectRecertSort)

  let [createOpen, setCreateOpen] = useState<string>()
  let [cyclePreFill, setCyclePreFill] = useState<RecertCycleType | undefined>()
  let [cyclePreFillTemplateID, setCyclePreFillTemplateID] = useState<
    string | undefined
  >()
  let [viewID, setViewID] = useState<string>()

  let isLoading = [
    licenseCyclesStatus,
    credentialCyclesStatus,
    nremtCyclesStatus,
  ].some((s) => s === 'pending')

  useAutoFetch(licenseCyclesStatus, fetchLicenseRecertificationCycles)
  useAutoFetch(credentialCyclesStatus, fetchCredentialRecertificationCycles)
  useAutoFetch(nremtCyclesStatus, fetchNREMTRecertificationCycles)
  useAutoFetch(
    useCompliance ? unstartedComplianceRecertItemsStatus : 'fulfilled',
    fetchUnstartedComplianceRecertItems,
  )

  let cycleDict: Record<typeof visibleType, RecertificationCycle[]> = {
    All: cycles,
    NREMT: nremtCycles,
    License: licenseCycles,
    Credential: credentialCycles,
  }
  let visibleCycles = cycleDict[visibleType]
    .filter(
      (c) =>
        c.name.toLowerCase().includes(cycleFilter.toLowerCase()) &&
        (statusFilter === 'All' || statusFilter === getCycleStatus(c)),
    )
    .toSorted((a, b) =>
      cycleSort === 'Expiration'
        ? dayjs(a.expirationDate).diff(b.expirationDate)
        : cycleSort === 'Alphabetical A-Z'
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name),
    )

  let ViewEntity = ({
    cycle,
  }: {
    cycle: RecertificationCycle
  }): ReactElement => {
    let [downloading, setDownloading] = useState<boolean>(false)
    let [viewBlob, setViewBlob] = useState<Blob | null>(null)

    let entityValues = (entity: typeof cycle.entity) => {
      if (entity.kind === 'License') {
        return {
          url: `/api/profile/license/image-url?licenseID=${entity.license.id}`,
          hasImage: !!entity.license.imageName,
        }
      }
      if (entity.kind === 'Credential') {
        return {
          url: `/api/v2/profile/credential/image-url?CredentialID=${entity.credential.id}`,
          hasImage: !!entity.credential.imageName,
        }
      }
      return {
        url: '/api/profile/nremt/image-url',
        hasImage: !!entity.nremt.imageName,
      }
    }

    useEffect(() => {
      let { url, hasImage } = entityValues(cycle.entity)
      if (hasImage && cycle.id === viewID) {
        setDownloading(true)
        axios
          .get(url)
          .then(pluckData)
          .then((data) =>
            axios.get<Blob>(data, { responseType: 'blob' }).then(pluckData),
          )
          .then((data) => setViewBlob(data))
          .catch((err) =>
            showError(
              err?.response?.data ||
                err?.message ||
                `Error Getting ${cycle.entity.kind}`,
            ),
          )
          .finally(() => setDownloading(false))
      }
    }, [cycle])

    return (
      <>
        <Dialog
          open={viewID === cycle.id}
          onClick={(e) => e.stopPropagation()}
          onClose={() => setViewID(undefined)}
          maxWidth="lg"
        >
          <Card
            variant="outlined"
            sx={{ p: 2, position: 'relative', overflow: 'scroll' }}
          >
            <IconButton
              sx={{ position: 'absolute', top: 8, right: 8 }}
              size="small"
              aria-label="Close cycle entity view"
              onClick={() => setViewID(undefined)}
            >
              <CloseIcon />
            </IconButton>
            <Typography
              sx={{
                textAlign: 'center',
                fontSize: 20,
                mb: 0.5,
              }}
            >
              View {cycle.entity.kind}
            </Typography>
            <Box>
              {cycle.entity.kind === 'License' && (
                <Box>
                  <Typography
                    align="center"
                    sx={{
                      fontSize: 18,
                    }}
                  >
                    #{cycle.entity.license.number} {'\u2022'}{' '}
                    {licenseTypeDict[cycle.entity.license.type.toLowerCase()] ||
                      cycle.entity.license.type}{' '}
                    {'\u2022'} {capitalize(cycle.entity.license.state)}
                  </Typography>
                </Box>
              )}
              {cycle.entity.kind === 'Credential' && (
                <Box>
                  <Typography
                    align="center"
                    sx={{
                      fontSize: 18,
                    }}
                  >
                    #{cycle.entity.credential.credentialIDNumber} {'\u2022'}{' '}
                    {typeof cycle.entity.credential.type === 'string'
                      ? cycle.entity.credential.type.toUpperCase()
                      : cycle.entity.credential.type.other}
                  </Typography>
                </Box>
              )}
              {cycle.entity.kind === 'NREMT' && (
                <Box>
                  <Typography
                    align="center"
                    sx={{
                      fontSize: 18,
                    }}
                  >
                    #{cycle.entity.nremt.nremtidNumber}
                  </Typography>
                </Box>
              )}
              <Box
                sx={{
                  mt: 1,
                  mb: 0.5,
                  display: 'flex',
                  justifyContent: 'center',
                  overflow: 'scroll',
                }}
              >
                {downloading && (
                  <Skeleton
                    variant="rounded"
                    sx={{ width: 400, height: 400 }}
                  />
                )}
                {viewBlob && (
                  <img
                    style={{ width: 400, overflow: 'scroll' }}
                    src={URL.createObjectURL(viewBlob)}
                    alt={`View ${cycle.entity.kind} Image`}
                  />
                )}
                {!downloading && !viewBlob && (
                  <Typography color="text.secondary">
                    This {cycle.entity.kind} does not have an associated image.
                  </Typography>
                )}
              </Box>
            </Box>
          </Card>
        </Dialog>
      </>
    )
  }

  return (
    <BasePage>
      {useRecertificationCycle && (
        <Box>
          <Typography variant="h4" component="h2">
            Recertification
          </Typography>
          <Typography
            color="text.secondary"
            variant="subtitle1"
            sx={{
              mt: 0.5,
            }}
          >
            Manage recertification cycles for your NREMT, licenses,
            certifications, & credentials
          </Typography>
          {useCompliance && unstartedComplianceRecertItems.length > 0 && (
            <Box sx={{ mt: 1, mb: 2 }}>
              {unstartedComplianceRecertItems.map((item) => {
                let { cycleType } = item
                let label =
                  cycleType.kind === 'nremt'
                    ? 'NREMT'
                    : cycleType.kind === 'credential'
                      ? `Credential: ${CREDENTIAL_TYPES.some((t) => t.toLowerCase() === cycleType.type.toLowerCase()) ? cycleType.type.toUpperCase() : cycleType.type}`
                      : `License: ${licenseTypeDict[cycleType.type.toLowerCase()] ?? cycleType.type} — ${capitalize(cycleType.state)}`
                return (
                  <Alert
                    key={item.id}
                    severity="warning"
                    sx={{ mb: 1 }}
                    action={
                      <Button
                        color="inherit"
                        size="small"
                        onClick={() => {
                          setCyclePreFill(item.cycleType)
                          setCyclePreFillTemplateID(item.templateID)
                          setCreateOpen(createCredential)
                        }}
                      >
                        Add Cycle
                      </Button>
                    }
                  >
                    Your manager requires a recertification cycle:{' '}
                    <strong>{label}</strong>
                  </Alert>
                )
              })}
            </Box>
          )}
          <Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-end',
                flexWrap: 'wrap-reverse',
                columnGap: 1,
                rowGap: 1.5,
                my: 1,
              }}
            >
              <TextField
                label="Search Cycle Name"
                size="small"
                value={cycleFilter}
                onChange={(e) => dispatch(recertSearchChanged(e.target.value))}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <FormControl sx={{ minWidth: 172 }}>
                <InputLabel id="cycle-type">Cycle Type</InputLabel>
                <Select
                  labelId="cycle-type"
                  value={visibleType}
                  label="Cycle Type"
                  onChange={(e) =>
                    dispatch(recertTypeFilterChanged(e.target.value))
                  }
                  size="small"
                >
                  <MenuItem value="All">All Cycles</MenuItem>
                  <MenuItem value="NREMT">NREMT Cycles</MenuItem>
                  <MenuItem value="License">License Cycles</MenuItem>
                  <MenuItem value="Credential">Credential Cycles</MenuItem>
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 172 }}>
                <InputLabel id="cycle-status">Cycle Status</InputLabel>
                <Select
                  labelId="cycle-status"
                  value={statusFilter}
                  label="Cycle Status"
                  onChange={(e) =>
                    dispatch(recertStatusFilterChanged(e.target.value))
                  }
                  size="small"
                >
                  <MenuItem value="All">All Cycles</MenuItem>
                  <MenuItem value="Active">Active Cycles</MenuItem>
                  <MenuItem value="Completed">Completed Cycles</MenuItem>
                  <MenuItem value="Archived">Archived Cycles</MenuItem>
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 172 }}>
                <InputLabel id="cycle-sort">Sort Cycles</InputLabel>
                <Select
                  labelId="cycle-sort"
                  value={cycleSort}
                  label="Sort Cycles"
                  onChange={(e) => dispatch(recertSortChanged(e.target.value))}
                  size="small"
                >
                  <MenuItem value="Expiration">Expiration</MenuItem>
                  <MenuItem value="Alphabetical A-Z">Alphabetical A-Z</MenuItem>
                  <MenuItem value="Alphabetical Z-A">Alphabetical Z-A</MenuItem>
                </Select>
              </FormControl>
              <Box sx={{ flexGrow: 1 }}></Box>
              <Button
                sx={{ alignSelf: 'flex-start' }}
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateOpen(createCredential)}
              >
                Add Cycle
              </Button>
            </Box>
            {!visibleCycles.length &&
              (!isLoading ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                    mt: 3,
                  }}
                >
                  <Box
                    sx={{
                      p: 4,
                      bgcolor: 'background.paperDark',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                      alignItems: 'center',
                    }}
                  >
                    <Avatar
                      sx={{
                        height: 72,
                        width: 72,
                        bgcolor: 'background.paper',
                      }}
                    >
                      <RecertificationIcon fontSize="large" color="primary" />
                    </Avatar>
                    <Typography variant="h6">
                      No{statusFilter === 'All' ? '' : ` ${statusFilter}`}{' '}
                      {visibleType === 'All' ? '' : ` ${visibleType}`}{' '}
                      Recertification Cycles Added Yet
                    </Typography>
                    <Typography
                      color="text.secondary"
                      variant="body1"
                      sx={{
                        textAlign: 'center',
                        maxWidth: 450,
                        lineHeight: '18px',
                      }}
                    >
                      Create your first cycle by clicking the button below. You
                      can add multiple cycles for different licenses,
                      certifications, and credentials, or even multiple cycles
                      for the same item if desired.
                    </Typography>
                    <Button
                      sx={{ py: 1.25, mt: 1.5 }}
                      variant="contained"
                      size="large"
                      startIcon={<AddIcon />}
                      onClick={() => setCreateOpen(createCredential)}
                    >
                      Add First Cycle
                    </Button>
                  </Box>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: 'background.paperDark',
                    }}
                  >
                    <Box sx={{ display: 'flex', gap: 0.5, mb: 2 }}>
                      <LightbulbIcon color="primary" />
                      <Typography>
                        Tips for Creating Recertification Cycles
                      </Typography>
                    </Box>{' '}
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns:
                          'repeat(auto-fit, minmax(240px, 1fr))',
                        gap: 2,
                      }}
                    >
                      <Box>
                        <Box
                          sx={{
                            color: 'primary.main',
                            display: 'flex',
                            gap: 0.5,
                            mb: 1,
                          }}
                        >
                          <ClockIcon />
                          <Typography>Dynamic Scheduling</Typography>
                        </Box>
                        <Typography color="text.secondary" variant="body2">
                          Set up recertification cycles according to the issue &
                          expiration dates of each item.
                        </Typography>
                      </Box>
                      <Box>
                        <Box
                          sx={{
                            color: 'primary.main',
                            display: 'flex',
                            gap: 0.5,
                            mb: 1,
                          }}
                        >
                          <CategoryIcon />
                          <Typography>Categorize Content</Typography>
                        </Box>
                        <Typography color="text.secondary" variant="body2">
                          Categorize your courses using any combination of our
                          preset categories & ones you create.
                        </Typography>
                      </Box>
                      <Box>
                        <Box
                          sx={{
                            color: 'primary.main',
                            display: 'flex',
                            gap: 0.5,
                            mb: 1,
                          }}
                        >
                          <CourseIcon />
                          <Typography>Allocate Courses</Typography>
                        </Box>
                        <Typography color="text.secondary" variant="body2">
                          Select content for your recertification cycle and
                          allocate each course's hours to the appropriate
                          category.
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    mt: 3,
                  }}
                >
                  <Skeleton
                    variant="rounded"
                    sx={{
                      width: '100%',
                      height: 84,
                    }}
                  />
                  <Skeleton
                    variant="rounded"
                    sx={{
                      width: '100%',
                      height: 84,
                    }}
                  />
                  <Skeleton
                    variant="rounded"
                    sx={{
                      width: '100%',
                      height: 84,
                    }}
                  />
                  <Skeleton
                    variant="rounded"
                    sx={{
                      width: '100%',
                      height: 84,
                    }}
                  />
                  <Skeleton
                    variant="rounded"
                    sx={{
                      width: '100%',
                      height: 84,
                    }}
                  />
                </Box>
              ))}
            <Box
              sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}
            >
              {visibleCycles.map((cycle) => (
                <Card
                  key={cycle.id}
                  component="div"
                  role="button"
                  tabIndex={0}
                  variant="outlined"
                  aria-label={`View ${cycle.name} recertification cycle`}
                  sx={{
                    py: 2,
                    px: 2.5,
                    bgcolor: 'background.paperDark',
                    '&:hover': {
                      cursor: 'pointer',
                      bgcolor: 'background.paper',
                    },
                    '&:focus-visible': {
                      outline: '2px solid',
                      outlineColor: 'primary.main',
                      outlineOffset: '2px',
                    },
                  }}
                  onClick={() =>
                    navigate(`/profile/recertification/cycle/${cycle.id}`)
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      navigate(`/profile/recertification/cycle/${cycle.id}`)
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Box sx={{ my: -1, mr: -0.25, ml: -1.25 }}>
                      <MultiCircularProgress
                        completed={cycle.completedHours || 0}
                        total={cycle.totalHours || 0}
                        title="Hours"
                      />
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 0.75,
                          alignItems: 'center',
                        }}
                      >
                        <Chip
                          size="small"
                          label={getCycleStatus(cycle)}
                          icon={cycle.complete ? <VerifiedIcon /> : undefined}
                          color={
                            cycle.complete
                              ? 'success'
                              : dayjs(cycle.expirationDate).isBefore(dayjs())
                                ? 'secondary'
                                : 'primary'
                          }
                        />
                        {cycle.templateID && (
                          <Tooltip title="Required by manager">
                            <ManagerIcon
                              fontSize="small"
                              color="secondary"
                              titleAccess="Required by manager"
                            />
                          </Tooltip>
                        )}
                        <Typography>{cycle.name}</Typography>
                      </Box>
                      <Box
                        sx={{
                          mt: 0.5,
                          display: 'flex',
                          flexWrap: 'wrap',
                          alignItems: 'center',
                          columnGap: 1.5,
                          rowGap: 0.5,
                          fontSize: 14,
                        }}
                      >
                        <Box>
                          <CalendarIcon
                            sx={{ height: 18, width: 18, mb: '2px', mr: '3px' }}
                          />
                          <Box
                            component="span"
                            sx={{
                              color: 'text.secondary',
                            }}
                          >
                            Issued {dayjs(cycle.issueDate).format('MM/DD/YYYY')}
                          </Box>
                        </Box>
                        <Box>
                          <AlarmIcon
                            sx={{ height: 18, width: 18, mb: '2px', mr: '3px' }}
                          />
                          <Box
                            component="span"
                            sx={{
                              color: 'text.secondary',
                            }}
                          >
                            Expires{' '}
                            {dayjs(cycle.expirationDate).format('MM/DD/YYYY')}
                          </Box>
                        </Box>
                        {cycle.entity.kind === 'License' && (
                          <Box
                            sx={{
                              display: 'flex',
                              gap: 0.5,
                              alignItems: 'center',
                            }}
                          >
                            <Box>
                              <InfoIcon
                                sx={{
                                  height: 18,
                                  width: 18,
                                  mb: '2px',
                                  mr: '3px',
                                }}
                              />
                              <Box
                                component="span"
                                sx={{ color: 'text.secondary' }}
                              >
                                {licenseTypeDict[
                                  cycle.entity.license.type.toLowerCase()
                                ] || cycle.entity.license.type}{' '}
                                {'\u2022'}{' '}
                                {capitalize(cycle.entity.license.state)}
                              </Box>
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </Box>
                    <Box>
                      <IconButton
                        aria-label={`View ${cycle.name} credential image`}
                        onClick={(e) => {
                          e.stopPropagation()
                          setViewID(cycle.id)
                        }}
                      >
                        <ZoomInIcon />
                      </IconButton>
                      <ViewEntity cycle={cycle} />
                    </Box>
                  </Box>
                </Card>
              ))}
            </Box>
            <CreateCycleForm
              key={`${createOpen}-${cyclePreFill?.kind}`}
              open={!!createOpen}
              setOpen={() => {
                setCreateOpen(undefined)
                setCyclePreFill(undefined)
                setCyclePreFillTemplateID(undefined)
              }}
              initialCycleType={cyclePreFill}
              templateID={cyclePreFillTemplateID}
            />
            {NotificationComponent}
          </Box>
        </Box>
      )}
    </BasePage>
  )
}
